import * as React from 'react'
import { Check, ChevronsUpDown, Plus } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { ButtonOption } from '@/types/mold'
import { getCreateOptionFunction } from '@/services/configRegistry'
import { getCreateErrorMessage } from '@/utils/errorHandling'
import ErrorNotificationModal from './ErrorNotificationModal'

/**
 * Simple SearchDropdown component with built-in search functionality.
 * 
 * Features:
 * - Built-in search/filtering functionality
 * - Fixed max height (300px) with scrollable content
 * - Loading states
 * - Error state styling
 * - Keyboard navigation support
 * - Create new option when no results found (extensible)
 */

interface NewSearchDropdownProps {
    /** Current selected value */
    value: any;
    /** Callback when selection changes */
    onChange: (value: any) => void;
    /** Current search term */
    searchTerm: string;
    /** Callback to update search term */
    setSearchTerm: (value: string) => void;
    /** Array of options to display */
    options: ButtonOption[];
    /** Placeholder text for the input */
    placeholder?: string;
    /** Callback when search filter changes */
    onFilterChange?: (searchTerm: string) => void;
    /** Whether the component is disabled */
    disabled?: boolean;
    /** Whether to show error styling */
    hasError?: boolean;
    /** Whether the component is in loading state */
    isLoading?: boolean;
    /** Custom loading message */
    loadingMessage?: string;
    /** Test ID identifier for testing purposes */
    testIdIdentifier?: string;
    /** Whether to allow creating new options */
    allowCreateNewOption?: boolean;
    /** Configuration for creating new options */
    createOptionConfig?: {
        entityType: string;
        onCreateSuccess?: (newOption: ButtonOption) => void;
    };
    /** Callback to refresh/refetch options after creation */
    onRefreshOptions?: () => void;
}

// Memoized option item component for better performance
const OptionItem = React.memo<{
    option: ButtonOption;
    isSelected: boolean;
    onSelect: () => void;
}>(({ option, isSelected, onSelect }) => (
    <CommandItem
        key={option.value}
        value={option.label}
        onSelect={onSelect}
        data-cy={`option-${option.label}`}
    >
        {option.label}
        <Check
            className={cn(
                'ml-auto text-blue-700',
                isSelected ? 'opacity-100' : 'opacity-0'
            )}
        />
    </CommandItem>
))

const NewSearchDropdown: React.FC<NewSearchDropdownProps> = ({
    value,
    onChange,
    searchTerm,
    setSearchTerm,
    options,
    placeholder,
    onFilterChange,
    disabled = false,
    hasError = false,
    isLoading = false,
    loadingMessage = "กำลังโหลด...",
    testIdIdentifier,
    allowCreateNewOption = false,
    createOptionConfig,
    onRefreshOptions
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false)
    const [isCreatingOption, setIsCreatingOption] = React.useState(false)
    const [errorModalOpen, setErrorModalOpen] = React.useState(false)
    const [errorMessage, setErrorMessage] = React.useState('')

    const handleOptionSelect = (option: ButtonOption) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsDropdownOpen(false);
    };

    const handleCreateNewOption = async () => {
        if (!allowCreateNewOption || !createOptionConfig || !searchTerm.trim()) {
            return;
        }

        setIsCreatingOption(true);
        try {
            const createOptionFunction = getCreateOptionFunction(createOptionConfig.entityType);
            if (!createOptionFunction) {
                const error = `ไม่พบฟังก์ชันสำหรับสร้าง ${createOptionConfig.entityType}`;
                setErrorMessage(error);
                setErrorModalOpen(true);
                return;
            }

            const newOption = await createOptionFunction(searchTerm.trim());

            // Call onCreateSuccess callback if provided
            if (createOptionConfig.onCreateSuccess) {
                createOptionConfig.onCreateSuccess(newOption);
            }

            // Refresh the options list
            if (onRefreshOptions) {
                onRefreshOptions();
            }

            // Select the newly created option
            onChange(newOption.value);
            setSearchTerm(newOption.label);
            setIsDropdownOpen(false);
        } catch (error) {
            console.error('Failed to create new option:', error);
            
            const errorMsg = getCreateErrorMessage(error, createOptionConfig.entityType);
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setIsCreatingOption(false);
        }
    };

    const handleCloseErrorModal = () => {
        setErrorModalOpen(false);
        setErrorMessage('');
    };

    const haveExactMatch = options.some(opt => opt.label.toLowerCase() === searchTerm.toLowerCase());

    // Filter options based on search term
    const filteredOptions = searchTerm.trim() === '' 
        ? options 
        : options.filter(option => 
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
          );

    const shouldShowCreateOption = allowCreateNewOption &&
        createOptionConfig &&
        searchTerm.trim() !== '' &&
        !haveExactMatch;

    return (
        <>
            <Popover open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant='outline'
                        role='combobox'
                        aria-expanded={isDropdownOpen}
                        disabled={disabled}
                        className={cn(
                            'w-full h-full rounded-lg justify-between py-2.5 focus:border-blue-500 focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
                            hasError ? 'border-red-500 focus-visible:border-red-500' : ''
                        )}
                        data-cy={`${testIdIdentifier}-dropdown`}
                    >
                        {options.find((option) => String(option.value) === String(value))?.label || placeholder}
                        <ChevronsUpDown className='opacity-50' />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className='w-[var(--radix-popover-trigger-width)] max-w-none p-0'>
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder={placeholder}
                            className='h-9'
                            value={searchTerm}
                            onValueChange={(search) => {
                                setSearchTerm(search);
                                onFilterChange?.(search);
                            }}
                            data-cy={`${testIdIdentifier}-search`}
                        />
                        <CommandList className="w-full max-h-[300px] overflow-y-auto">
                            {isLoading ? (
                                <div className="px-2 py-6 text-center text-muted-foreground text-sm">
                                    {loadingMessage}
                                </div>
                            ) : (
                                <>
                                    {/* Show "no options found" only when there are no filtered options AND no create option */}
                                    {filteredOptions.length === 0 && !shouldShowCreateOption && (
                                        <CommandEmpty>ไม่พบตัวเลือก</CommandEmpty>
                                    )}

                                    {/* Render filtered options if any exist */}
                                    {filteredOptions.length > 0 && (
                                        <CommandGroup>
                                            {filteredOptions.map((option) => (
                                                <OptionItem
                                                    key={option.value}
                                                    option={option}
                                                    isSelected={String(value) === String(option.value)}
                                                    onSelect={() => handleOptionSelect(option)}
                                                />
                                            ))}
                                        </CommandGroup>
                                    )}

                                    {/* Render create option if conditions are met */}
                                    {shouldShowCreateOption && (
                                        <CommandGroup>
                                            <CommandItem
                                                value={`สร้าง ${searchTerm}`}
                                                onSelect={handleCreateNewOption}
                                                disabled={isCreatingOption}
                                                className="text-blue-600 font-medium"
                                                data-cy={`${testIdIdentifier}-create-option`}
                                            >
                                                <Plus className="mr-2 h-4 w-4" />
                                                {isCreatingOption ? 'กำลังสร้าง...' : `สร้าง "${searchTerm}"`}
                                            </CommandItem>
                                        </CommandGroup>
                                    )}
                                </>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {/* Error Modal */}
            <ErrorNotificationModal
                isOpen={errorModalOpen}
                onClose={handleCloseErrorModal}
                errorMessage={errorMessage}
            />
        </>
    )
}

export default NewSearchDropdown;